import { useState } from 'react';
import { Button } from '@reflex/ui/components/ui/button';
import { Textarea } from '@reflex/ui/components/ui/textarea';
import { Badge } from '@reflex/ui/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@reflex/ui/components/ui/collapsible';
import { PanelTitle, SectionLabel } from '@reflex/ui/components/layout/Typography';
import {
  CheckCircle2,
  XCircle,
  ChevronDown,
  ExternalLink,
  UserCheck,
  FileText,
} from 'lucide-react';
import { cn } from '@reflex/ui/lib/utils';
import { JsonSyntax } from '@reflex/ui/components/content/log-viewer';
import { MarkdownContent } from '@reflex/ui/components/content/MarkdownContent';

interface ApprovalDecisionOption {
  value: string;
  label: string;
}

/**
 * The card renders exactly the assignee-facing gate payload: the
 * author-templated prompt plus the single predecessor output (`context`).
 * Wider run data (other node outputs, flow state, trigger data) is not part
 * of the gate — authors template what reviewers need into the prompt.
 */
interface FlowApprovalCardProps {
  runId: string;
  nodeId: string;
  nodeName: string;
  approvalType: string;
  prompt?: string;
  context?: Record<string, unknown> | null;
  allowFeedback?: boolean;
  decisionOptions?: ApprovalDecisionOption[];
  onSubmit: (response: { approved?: boolean; decision?: string; feedback?: string }) => void;
  isSubmitting?: boolean;
}

function isUrl(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function ContextValue({ label, value }: { label: string; value: unknown }) {
  if (value === null || value === undefined) return null;

  if (isUrl(value)) {
    return (
      <div className="flex items-baseline gap-2">
        <span className="shrink-0 text-xs font-medium text-muted-foreground">{label}:</span>
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 truncate text-xs text-status-info hover:underline"
        >
          {value}
          <ExternalLink className="h-3 w-3 shrink-0" />
        </a>
      </div>
    );
  }

  if (typeof value === 'string') {
    return (
      <div>
        <span className="text-xs font-medium text-muted-foreground">{label}:</span>
        <MarkdownContent content={value} size="xs" className="mt-0.5" />
      </div>
    );
  }

  return (
    <div>
      <span className="text-xs font-medium text-muted-foreground">{label}:</span>
      <pre className="mt-0.5 overflow-auto rounded bg-muted/50 p-1.5 text-xs">
        <JsonSyntax value={value} />
      </pre>
    </div>
  );
}

function SectionHeader({
  icon: Icon,
  iconClassName,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  iconClassName?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon className={cn('h-3 w-3 text-muted-foreground/60', iconClassName)} />
      <SectionLabel as="span" className="text-[10px]">
        {children}
      </SectionLabel>
    </div>
  );
}

function PreviousOutputSection({ context }: { context: Record<string, unknown> }) {
  const knownKeys = [
    'summary',
    'plan',
    'verdict',
    'issues',
    'prUrl',
    'prNumber',
    'message',
    'error',
  ];
  const known: Array<[string, unknown]> = [];
  const other: Array<[string, unknown]> = [];

  for (const [key, value] of Object.entries(context)) {
    if (value === null || value === undefined) continue;
    if (knownKeys.includes(key)) {
      known.push([key, value]);
    } else {
      other.push([key, value]);
    }
  }

  if (known.length === 0 && other.length === 0) return null;

  const sortOrder = new Map(knownKeys.map((k, i) => [k, i]));
  known.sort((a, b) => (sortOrder.get(a[0]) ?? 99) - (sortOrder.get(b[0]) ?? 99));

  return (
    <section className="space-y-1.5">
      <SectionHeader icon={FileText} iconClassName="text-status-info/70">
        Previous Step Output
      </SectionHeader>
      <div className="space-y-2 rounded-lg border border-border/50 bg-card/60 p-3 shadow-xs">
        {known.map(([key, value]) => (
          <ContextValue key={key} label={key} value={value} />
        ))}
        {other.map(([key, value]) => (
          <ContextValue key={key} label={key} value={value} />
        ))}
      </div>
    </section>
  );
}

export function FlowApprovalCard({
  nodeName,
  approvalType,
  prompt,
  context,
  allowFeedback = true,
  decisionOptions,
  onSubmit,
  isSubmitting = false,
}: FlowApprovalCardProps) {
  const [feedback, setFeedback] = useState('');
  const [rawOpen, setRawOpen] = useState(false);

  const hasContext = context && Object.keys(context).length > 0;

  return (
    <div className="relative overflow-hidden rounded-xl border border-border/70 bg-card shadow-md ring-1 ring-status-warning/5">
      {/* Top accent stripe */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-status-warning/60 to-transparent"
      />

      {/* Header */}
      <div className="relative border-b border-border/60 bg-gradient-to-br from-status-warning/8 via-status-warning/3 to-transparent px-4 py-3.5">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-status-warning/30 bg-status-warning/10 text-status-warning shadow-xs ring-1 ring-inset ring-status-warning/20">
            <UserCheck className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            <PanelTitle as="h3" size="sm" className="leading-tight">
              {nodeName}
            </PanelTitle>
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                className="border-status-warning/30 bg-status-warning/10 px-1.5 py-0 text-[10px] font-medium capitalize text-status-warning"
              >
                {approvalType.replace(/_/g, ' ')}
              </Badge>
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground/60">
                Awaiting review
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-h-[60vh] space-y-4 overflow-y-auto px-4 py-4">
        {prompt && (
          <div className="rounded-lg border border-border/50 bg-muted/10 px-3.5 py-3 shadow-xs">
            <MarkdownContent content={prompt} size="sm" className="text-foreground/90" />
          </div>
        )}

        {hasContext && <PreviousOutputSection context={context!} />}

        {hasContext && (
          <div className="space-y-1.5 border-t border-border/40 pt-3">
            <Collapsible open={rawOpen} onOpenChange={setRawOpen}>
              <CollapsibleTrigger className="flex w-full items-center gap-1.5 rounded-md px-1 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground">
                <ChevronDown
                  className={cn('h-3 w-3 transition-transform', rawOpen && 'rotate-180')}
                />
                Show raw data
              </CollapsibleTrigger>
              <CollapsibleContent>
                <pre className="mt-1.5 max-h-48 overflow-auto rounded-lg border border-border/40 bg-muted/40 p-2.5 text-[11px]">
                  <JsonSyntax value={{ context }} />
                </pre>
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="space-y-3 border-t border-border/60 bg-muted/20 px-4 py-3.5">
        {allowFeedback && (
          <Textarea
            placeholder="Feedback (optional)"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={3}
            className="resize-none border-border/60 bg-card text-sm shadow-xs focus-visible:ring-status-warning/30"
          />
        )}
        {decisionOptions && decisionOptions.length > 0 ? (
          <div className="grid gap-2">
            {decisionOptions.map((option) => {
              const variant =
                option.value === 'close_issue'
                  ? 'destructive'
                  : option.value === 'end_flow'
                    ? 'secondary'
                    : 'default';
              return (
                <Button
                  key={option.value}
                  size="sm"
                  variant={variant}
                  disabled={isSubmitting}
                  className="shadow-xs"
                  onClick={() =>
                    onSubmit({
                      decision: option.value,
                      approved: option.value !== 'end_flow',
                      feedback: feedback || undefined,
                    })
                  }
                >
                  {option.value === 'close_issue' ? (
                    <XCircle className="mr-2 h-4 w-4" />
                  ) : (
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                  )}
                  {option.label}
                </Button>
              );
            })}
          </div>
        ) : (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="default"
              className="flex-1 shadow-xs"
              disabled={isSubmitting}
              onClick={() => onSubmit({ approved: true, feedback: feedback || undefined })}
            >
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Approve
            </Button>
            <Button
              size="sm"
              variant="destructive"
              className="flex-1 shadow-xs"
              disabled={isSubmitting}
              onClick={() => onSubmit({ approved: false, feedback: feedback || undefined })}
            >
              <XCircle className="mr-2 h-4 w-4" />
              Reject
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
